import { VideoJsAudioPlayerSkeleton } from '@app/core/playback/audio/VideoJsAudioPlayerSkeleton';
import { ExclusivePlaybackMode } from '@app/core/playback/store/base-playback-slice';
import mime from 'mime';
import { UrlMediaPlayback } from './UrlMediaPlayback';
import { usePlayableAudioSrc } from './usePlayableAudioSrc';

export interface UrlMediaEmbedProps {
  /** Media file URL to play. */
  url: string;
  /** MIME type; inferred from URL when omitted. */
  type?: string | null;
  /** Optional identifier for this playback instance in the registry. */
  playbackId?: string;
  exclusivePlaybackMode?: ExclusivePlaybackMode;
  /** When true, playback starts as soon as the source is ready. */
  autoPlay?: boolean;
  /** Called when playback starts. */
  onPlay?: () => void;
  /** Called when playback reaches the end of the media. */
  onEnded?: () => void;
}

/**
 * Embeddable audio/video player for a file URL. Prepares playable audio (MPEG-in-WAV /
 * MP2) then plays through {@link UrlMediaPlayback} so exclusive play works with other
 * registered players.
 */
export function UrlMediaEmbed({
  url,
  type: typeProp,
  playbackId,
  exclusivePlaybackMode,
  autoPlay = false,
  onPlay,
  onEnded,
}: UrlMediaEmbedProps) {
  const type = typeProp ?? mime.getType(url);
  const isAudio = Boolean(type?.startsWith('audio/'));

  const {
    src: playableSrc,
    playType,
    loading,
  } = usePlayableAudioSrc(isAudio ? url : null, type ?? null);

  if (!type) {
    return <>No player found for type</>;
  }

  if (isAudio) {
    if (loading) {
      return <VideoJsAudioPlayerSkeleton />;
    }

    if (!playableSrc) {
      return null;
    }

    return (
      <UrlMediaPlayback
        src={playableSrc}
        sourceType={playType ?? type}
        playbackId={playbackId}
        exclusivePlaybackMode={exclusivePlaybackMode}
        autoPlay={autoPlay}
        onPlay={onPlay}
        onEnded={onEnded}
      />
    );
  }

  if (type.startsWith('video/')) {
    return (
      <UrlMediaPlayback
        src={url}
        sourceType={type}
        playbackId={playbackId}
        exclusivePlaybackMode={exclusivePlaybackMode}
        autoPlay={autoPlay}
        onPlay={onPlay}
        onEnded={onEnded}
      />
    );
  }

  return <>No player found for type</>;
}
